// Taşeron portalı — Panel: "Aktif Çalışma İzinleri" (api-contract §28.3 `upcomingPermits`),
// KOMPAKT TABLO sunumu — 9 sütun: hazırlık, çalışma izni, tehlike, geçerlilik, personel,
// belge, eğitim, uyumluluk, durum.
//
// **Tarihçe.** Bu kart bir liste-satırı tasarımından (ikon + başlık/subtitle + metrik satırı)
// tabloya GEÇTİ — owner iki tasarımı yan yana A/B karşılaştırıp (2026-08-16) TABLOYU seçti.
// Eski liste tasarımı SİLİNDİ (git geçmişinde durur); bu dosya artık panonun TEK "Aktif Çalışma
// İzinleri" yüzeyi. Tasarım gerekçesi (sütun sırası, yoğunluk, `@container` disclosure bantları (S37-3: 46rem/62rem, panel genişliği),
// uyumluluk çubuğunun `sr-only <progress>` deseni): `docs/reviews/permits-table-design.md`.
// Backend/sözleşme AYNI (`dto.upcomingPermits`, §28.3) — yalnız SUNUM değişti.
//
// **Küme + sıra + üst sınır SUNUCUDADIR** (§28.3 "Sıra + üst sınır (upcomingPermits)" — temel
// küme §28.1(0), `validityStart` artan, `Settings:SupplierDashboard:MaxUpcomingPermits`
// varsayılan 5); istemci burada YENİDEN filtrelemez/sıralamaz/SIRALANABİLİR başlık taşımaz —
// sütun başlıkları İNERT etikettir. Sayaç rozeti/alt başlık owner kararıyla KALKTI, geri
// EKLENMEZ.
//
// **İki alt sayı ASLA toplanmaz/farkı alınmaz** (§28.3 "İki alt sayı BAĞIMSIZ DEĞİLDİR ve
// TOPLANMAZ") — `documentsSatisfiedPersonnelCount`/`trainingsCompletePersonnelCount` aynı
// kişiyi iki AYRI boyuttan (belge/eğitim) sayar, kümeler örtüşür. Fraksiyonlar HER ZAMAN kendi
// paydası (`personnelCount`) ile basılır (`8/12`, çıplak `8` DEĞİL) — payda olmadan yan yana
// üç sayı "toplanabilir" görünürdü. Uyumluluk sütunu yalnız `readyPersonnelCount`'tan kurulur,
// bu iki alt sayının TOPLAMI/FARKI DEĞİL.
//
// ⚠️ **Amber (`text-warning`/`text-warning-foreground`) hiçbir yerde bağımsız bir durum
// ikonu/metin rengi DEĞİLDİR** — depo kuralı: o token koyu temada `bg-surface` (grafit)
// üstünde ~1.00:1 verir, glif kaybolur (RiskWeekCard.tsx/PendingActionsCard.tsx aynı not —
// ikisi kalktı ama kural KALICI). Amber YALNIZ uyumluluk çubuğunun DOLGUSU (`bg-warning`);
// çubuğun `%` METNİ HER ZAMAN `text-fg`. Takılı readiness durumu `text-danger` (bu depoda
// bağımsız kullanımı AA doğrulanmış, `DocumentsPage.tsx`'in sil ikonu emsali), amber DEĞİL —
// aynı kural karşılanmamış belge/eğitim fraksiyonlarının vurgusuna da uygulanır (aşağıda,
// `documentsUnmet`/`trainingsUnmet`).
//
// **Renk hiçbir zaman TEK taşıyıcı değildir (WCAG 1.4.1)** — readiness ikonun KENDİ ŞEKLİ
// (Check/Uyarı/Info) + `Tooltip`'in gerçek `aria-label`'ı koşulu ikonu AÇMADAN da taşır;
// uyumluluk çubuğu görünür `%` sayısı + `sr-only <progress>` ile taşınır; karşılanmamış
// fraksiyonun kırmızısı REDÜNDANT vurgudur — `8/12` metninin kendisi zaten koşulu yazıyla
// anlatır (tek taşıyıcı DEĞİL).

import type {
  PermitStatus,
  SupplierDashboardDto,
  SupplierUpcomingPermitDto,
} from '@isgportal/shared';
import {
  EmptyState,
  HazardTag,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  Tooltip,
  cn,
} from '@isgportal/shared';
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  GraduationCap,
  Info,
  type LucideIcon,
  TriangleAlert,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { type DashboardClock, absoluteDayLabel } from '../../lib/dashboardTime';
import { permitStatusPresentation } from '../status/tone';
import { DashboardPanel, PanelHeader } from './PanelHeader';

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/** Boş personel kümesinde 5–8. sütunlar bu app'in yerleşik boş değeriyle basılır —
 *  `Math.round((0/0)*100)` `NaN` üretirdi, sütun 1 (readiness ikonu) zaten "Personel yok" der. */
const EMPTY_CELL = '—';

interface Readiness {
  icon: LucideIcon;
  /** `text-*` — asla `text-warning`/`text-warning-foreground` (dosya başı amber notu). */
  className: string;
  label: string;
  content: ReactNode;
}

/** Readiness ikonu + `Tooltip` içeriği — hem sütun 1'in hücresi hem alt satırdaki subtitle'ın
 *  KENDİSİ YOK (o yalnız tarih basar), yalnız bu kart içinde kullanılır (tek çağıran). */
function readinessOf(permit: SupplierUpcomingPermitDto, t: TFunction): Readiness {
  if (permit.personnelCount === 0) {
    const text = t('permits.readiness.noPersonnel');
    // Tooltip'in tetikleyici tabanı zaten `text-muted` taşır (Tooltip.tsx `triggerBase`) —
    // burada override GEREKMEZ.
    return { icon: Info, className: '', label: text, content: text };
  }

  // İki boyut HER ZAMAN birlikte basılır (dosya başı notu) — hangisi/hangileri takılı
  // olursa olsun aynı içerik, yalnız ikon/renk/erişilebilir ad HAZIR/TAKILI arasında değişir.
  const content = (
    <div className="flex flex-col gap-0.5">
      <p>
        {t('dashboard.permits.blockedDocuments', {
          ready: permit.documentsSatisfiedPersonnelCount,
          total: permit.personnelCount,
        })}
      </p>
      <p>
        {t('dashboard.permits.blockedTrainings', {
          ready: permit.trainingsCompletePersonnelCount,
          total: permit.personnelCount,
        })}
      </p>
    </div>
  );

  const documentsBlocking = permit.documentsSatisfiedPersonnelCount < permit.personnelCount;
  const trainingsBlocking = permit.trainingsCompletePersonnelCount < permit.personnelCount;
  if (!documentsBlocking && !trainingsBlocking) {
    // Sınıflar TAM YAZILIR (Tailwind statik tarama gerektirir; dinamik `` `hover:${x}` ``
    // ÜRETMEZ) — `twMerge` (Tooltip.tsx `cn`) bunları `triggerBase`'in `text-muted
    // hover:text-fg`'sinin ÜZERİNE yazar.
    return {
      icon: CheckCircle2,
      className: 'text-success hover:text-success',
      label: t('dashboard.permits.ready'),
      content,
    };
  }
  // Takılı — kırmızı, amber DEĞİL (dosya başı notu).
  return {
    icon: TriangleAlert,
    className: 'text-danger hover:text-danger',
    label: t('dashboard.status.notEntryReady'),
    content,
  };
}

export function UpcomingPermitsCard({
  dto,
  culture,
}: { dto: SupplierDashboardDto; culture: string }) {
  const { t } = useTranslation();
  const clock: DashboardClock = { asOf: dto.asOf, timezone: dto.timezone };

  return (
    <DashboardPanel>
      <PanelHeader icon={ClipboardCheck} title={t('dashboard.permits.title')} />

      {dto.upcomingPermits.length === 0 ? (
        // Başlık `<table>` DIŞINDA yaşıyor, boş tabloyu çıplak `<thead>` bandıyla göstermek
        // anlamsız olurdu → panel variant'lı `EmptyState` (metin-yalnız, sol hizalı).
        <EmptyState variant="panel" title={t('dashboard.permits.empty')} className="px-4 py-4" />
      ) : (
        // Shared `Table` sarmalayıcısı KULLANILMAZ — kendi `border`'ı panel kabuğuyla ÇATIŞIR. Yatay taşma yine de kendi kabında kaydırılır
        // (`overflow-x-auto`, sayfa gövdesi kaymaz) — bant genişliği fiziksel imkânsız olan dar
        // viewport'larda TEK kaçış yolu bu (aşağıdaki disclosure bantlarının floor'u).
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{t('dashboard.permits.title')}</caption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className="pl-5 pr-2 py-2 text-center">
                  <span className="sr-only">{t('dashboard.permits.table.col.readiness')}</span>
                </TableHead>
                <TableHead scope="col" className="px-2 py-2">
                  {t('dashboard.permits.table.col.title')}
                </TableHead>
                <TableHead scope="col" className="hidden px-2 py-2 @[62rem]:table-cell">
                  {t('dashboard.permits.table.col.hazard')}
                </TableHead>
                <TableHead scope="col" className="hidden px-2 py-2 @[62rem]:table-cell">
                  {t('dashboard.permits.table.col.validity')}
                </TableHead>
                <TableHead scope="col" className="px-2 py-2 text-right">
                  <Tooltip
                    icon={Users}
                    label={t('dashboard.permits.metrics.personnelLabel')}
                    content={t('dashboard.permits.table.col.personnelHint')}
                  />
                </TableHead>
                <TableHead scope="col" className="hidden px-2 py-2 text-right @[46rem]:table-cell">
                  <Tooltip
                    icon={FileText}
                    label={t('dashboard.permits.metrics.documentsLabel')}
                    content={t('dashboard.permits.table.col.documentsHint')}
                  />
                </TableHead>
                <TableHead scope="col" className="hidden px-2 py-2 text-right @[46rem]:table-cell">
                  <Tooltip
                    icon={GraduationCap}
                    label={t('dashboard.permits.metrics.trainingsLabel')}
                    content={t('dashboard.permits.table.col.trainingsHint')}
                  />
                </TableHead>
                <TableHead scope="col" className="w-[7.5rem] px-2 py-2">
                  <span className="inline-flex items-center gap-1">
                    {t('dashboard.permits.metrics.complianceLabel')}
                    <Tooltip
                      icon={Info}
                      label={t('dashboard.permits.table.col.complianceHint')}
                      content={t('dashboard.permits.table.col.complianceHint')}
                    />
                  </span>
                </TableHead>
                <TableHead scope="col" className="pl-2 pr-5 py-2">
                  {t('dashboard.permits.table.col.status')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dto.upcomingPermits.map((permit) => (
                <PermitRow
                  key={permit.uuid}
                  permit={permit}
                  clock={clock}
                  culture={culture}
                  t={t}
                />
              ))}
            </TableBody>
          </table>
        </div>
      )}

      {/* Alt bağlantı — gövdedeki satırlarla AYNI ritimde bir SATIR — `border-t` + satırların
          `px-5 py-2.5` yoğunluğu. */}
      <Link
        to="/permits"
        className="flex items-center gap-1.5 border-t border-border px-4 py-2.5 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {t('dashboard.permits.viewAll')}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </DashboardPanel>
  );
}

function PermitRow({
  permit,
  clock,
  culture,
  t,
}: {
  permit: SupplierUpcomingPermitDto;
  clock: DashboardClock;
  culture: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const readiness = readinessOf(permit, t);
  const Icon = readiness.icon;
  const status = permitStatusPresentation(permit.status as PermitStatus);
  const hasPersonnel = permit.personnelCount > 0;

  // Uyumluluk çubuğu — `readyPersonnelCount / personnelCount` (§28.1(1)); dosya başı notu,
  // "TOPLANMAZ" uyarısı. `personnelCount === 0` iken bu blok hiç ÇALIŞTIRILMAZ (`hasPersonnel`
  // koruması) — `NaN` üretmez.
  const compliancePercent = hasPersonnel
    ? Math.round((permit.readyPersonnelCount / permit.personnelCount) * 100)
    : 0;
  // S6 (design review): kısmi dolgu gri — amber `#f1c21b` `#e0e0e0` iz üstünde 1.28:1.
  const complianceBarClassName =
    permit.readyPersonnelCount === permit.personnelCount
      ? 'bg-success'
      : permit.readyPersonnelCount === 0
        ? 'bg-danger'
        : 'bg-muted';
  const complianceTooltip = t('dashboard.permits.metrics.complianceTooltip', {
    ready: permit.readyPersonnelCount,
    total: permit.personnelCount,
    percent: compliancePercent,
  });

  // Belge/eğitim şartı KARŞILANMAMIŞ fraksiyon (`ready < total`) kırmızıyla VURGULANIR. Amber
  // DEĞİL (dosya başı amber notu) — `text-danger` bu depoda bağımsız kullanımda AA
  // DOĞRULANMIŞ (`DocumentsPage.tsx` sil ikonu emsali). TEK eşik — `0/1` ile `1/4` AYNI
  // muamele görür, ikinci bir "kısmi" katman İCAT EDİLMEZ (renk zaten "karşılanmadı"
  // durumunun İKİNCİL vurgusu, TEK taşıyıcısı değil — fraksiyon metninin kendisi zaten
  // koşulu yazıyla anlatır, WCAG 1.4.1). `hasPersonnel` false iken bu iki bayrak hiç
  // OKUNMAZ (o durumda hücre `—`, renksiz).
  const documentsUnmet = permit.documentsSatisfiedPersonnelCount < permit.personnelCount;
  const trainingsUnmet = permit.trainingsCompletePersonnelCount < permit.personnelCount;

  return (
    <TableRow className="carbon:h-auto carbon:min-h-[var(--row-h)]">
      <TableCell className="pl-5 pr-2 carbon:py-2 text-center">
        <Tooltip
          icon={Icon}
          label={readiness.label}
          content={readiness.content}
          className={readiness.className}
        />
      </TableCell>
      <TableCell truncate={false} className="px-2 carbon:py-2">
        {/* Sütun genişliği `min-w-[7rem] max-w-[16rem]`'iyle sınırlanır (owner bulgusu,
            2026-08-16 dördüncü tur — "sütun çok geniş", beşinci tur — "160px çok erken
            kırpıyor, genişlet"). Erken bir sürümde `<td>` üzerinde `w-full max-w-0` hilesi
            vardı; bu YALNIZ ekstra yatay boşluk YOKKEN çalışıyordu — boş sağ alan bolken (ör.
            1920px, seyrek sütunlar) auto table layout `max-width:0`'ı bir üst sınır olarak
            DAYATMIYOR, sütun tam metne kadar büyüyor ve `truncate` HİÇ tetiklenmiyordu (Chrome/
            Playwright ölçümüyle DOĞRULANDI). `max-w-[16rem]` (256px) hem `<Link>`'i hem
            alttaki subtitle'ı SARAN `<div>`'e konur; normal bir blok elemanının `max-width`'i
            olduğu için tablo auto-layout'unun "boş alanı dağıt" davranışından ETKİLENMEZ.
            256px gerçekçi örnek başlıkların İKİSİNİ de ("Cephe İskelesi Montajı — B Blok" →
            209px doğal genişlik, "Sıcak Çalışma İzni — Tank Sahası" → 216px) SONUNA kadar TAM
            gösterir, gerçekten uzun bir başlıkta (99 karakter) hâlâ `scrollWidth(644) >
            clientWidth(256)` — kırpma ÇALIŞIR (ölçümle doğrulandı). `min-w-[7rem]` (112px) —
            max-width TEK BAŞINA sütunün ALT sınırını 0'a düşürür (`overflow:hidden` içerik
            auto-layout'ta minimum genişlik İSTEMEZ), taban + tavan çifti sütunü [112px, 256px]
            aralığına SABİTLER. 2xl kart genişliğinde (823px, TÜM 9 sütun + en geniş tarih
            aralığı) gerçek bir Playwright ölçümüyle DOĞRULANDI: tablo 821px'te KALIYOR
            (taşma/scrollbar YOK), sütun kendiliğinden 209px'e oturuyor (taban-tavan arasında,
            elastik). Tam metin yine de `title` özniteliğinde (native tooltip) KAYBOLMAZ. */}
        <div className="min-w-[7rem] max-w-[16rem]">
          <Link
            to={`/permits/${permit.uuid}`}
            title={permit.title}
            className="block truncate rounded text-sm font-medium text-fg hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {permit.title}
          </Link>
          {/* Gizlenen `hazard`/`validity` sütunlarının YERİNE geçer — yalnız MUTLAK başlangıç
              TARİHİ (`absoluteDayLabel`, saat YOK, "bugün"/"yarın" YOK) — geniş `validity`
              sütunuyla AYNI rejim (owner isteği, 2026-08-16 dördüncü tur: "yarın 15:00" gibi
              göreceli/saatli bir tarih, mutlak bir aralığın yanında karışık okunuyordu; dar
              genişlikte de aynı disiplin uygulanır). Alt satır yalnız BAŞLANGIÇ taşır, bitiş
              burada YOK — dar genişlikte tek satırlık yer var, aralığın tamamı yalnız geniş
              `validity` sütununda. */}
          <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-muted @[62rem]:hidden">
            <HazardTag hazardClass={permit.hazardClass} className="shrink-0" />
            <span className="truncate tnum">
              {absoluteDayLabel(permit.validityStart, clock.timezone, culture)}
            </span>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden whitespace-nowrap px-2 carbon:py-2 @[62rem]:table-cell">
        <HazardTag hazardClass={permit.hazardClass} />
      </TableCell>
      <TableCell className="hidden whitespace-nowrap px-2 carbon:py-2 tnum text-muted @[62rem]:table-cell">
        {/* Geçerlilik ARALIĞI — iki uç SİMETRİK, HER İKİSİ de mutlak tarih (`absoluteDayLabel`),
            HER İKİSİ de AYNI punto/ağırlık/renk (owner isteği, 2026-08-16 dördüncü tur —
            görecelli "yarın 15:00" + ikincil küçük bitiş tedavisi REDDEDİLDİ, simetri seçildi;
            bu, "relatif aciliyet ipucu 2xl'de kaybolur" ödünleşimini BİLEREK simetri LEHİNE
            çözer). Saat YOK — yalnız tarih. İki tarih TEK satırda kalır (`whitespace-nowrap`)
            — 2xl'deki 32px tek-satır satır yüksekliği bütçesi bozulmaz. */}
        {absoluteDayLabel(permit.validityStart, clock.timezone, culture)} –{' '}
        {absoluteDayLabel(permit.validityEnd, clock.timezone, culture)}
      </TableCell>
      <TableCell className="px-2 carbon:py-2 text-right tnum">
        {hasPersonnel ? permit.personnelCount : <span className="text-muted">{EMPTY_CELL}</span>}
      </TableCell>
      <TableCell className="hidden px-2 carbon:py-2 text-right tnum @[46rem]:table-cell">
        {hasPersonnel ? (
          <span className={documentsUnmet ? 'text-danger font-medium' : undefined}>
            {permit.documentsSatisfiedPersonnelCount}/{permit.personnelCount}
          </span>
        ) : (
          <span className="text-muted">{EMPTY_CELL}</span>
        )}
      </TableCell>
      <TableCell className="hidden px-2 carbon:py-2 text-right tnum @[46rem]:table-cell">
        {hasPersonnel ? (
          <span className={trainingsUnmet ? 'text-danger font-medium' : undefined}>
            {permit.trainingsCompletePersonnelCount}/{permit.personnelCount}
          </span>
        ) : (
          <span className="text-muted">{EMPTY_CELL}</span>
        )}
      </TableCell>
      <TableCell className="w-[7.5rem] px-2 carbon:py-2">
        {hasPersonnel ? (
          <span className="flex items-center gap-2">
            <progress
              className="sr-only"
              value={compliancePercent}
              max={100}
              aria-label={complianceTooltip}
            />
            <span
              className="h-2 min-w-[3rem] flex-1 overflow-hidden bg-surface-muted"
              aria-hidden="true"
            >
              <span
                className={cn('block h-full', complianceBarClassName)}
                style={{ width: `${compliancePercent}%` }}
              />
            </span>
            <span className="w-9 shrink-0 text-right text-xs font-medium tnum text-fg">
              {compliancePercent}%
            </span>
          </span>
        ) : (
          <span className="text-muted">{EMPTY_CELL}</span>
        )}
      </TableCell>
      <TableCell className="pl-2 pr-5 carbon:py-2">
        <Tag color={status.color} title={t(status.labelKey)}>
          {t(status.labelKey)}
        </Tag>
      </TableCell>
    </TableRow>
  );
}
